// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import type { Document } from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { ShowInAppClient } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface DocumentsRowActionsProps {
  readonly document: Document;
  readonly onDownload: (document: Document) => void;
  readonly onViewDetail: (document: Document) => void;
  readonly onDelete: (document: Document) => void;
}

export const DocumentsRowActions: FC<DocumentsRowActionsProps> = ({
  document,
  onDownload,
  onViewDetail,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isDeleteDisabled = document.scope === DocumentScope.SilverAlpha;

  const onViewDetailClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onViewDetail(document);
    },
    [document, onViewDetail],
  );

  const onDownloadClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDownload(document);
    },
    [document, onDownload],
  );

  const onDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete(document);
    },
    [document, onDelete],
  );

  return (
    <div
      className="flex items-center justify-end gap-x-4"
      data-stop-row-click
      onClick={event => event.stopPropagation()}
    >
      <Tooltip
        title={t('documents.row-actions.view-detail', { ns: 'components' })}
        placement="top"
      >
        <span>
          <IconButton
            icon="Eye"
            variant="icon"
            size="lg"
            color="main"
            onClick={onViewDetailClick}
            data-testid={`documents-row-view-${document.id}`}
          />
        </span>
      </Tooltip>

      <Tooltip
        title={t('documents.row-actions.download', { ns: 'components' })}
        placement="top"
      >
        <span>
          <IconButton
            icon="Download"
            variant="icon"
            size="lg"
            color="main"
            onClick={onDownloadClick}
            data-testid={`documents-row-download-${document.id}`}
          />
        </span>
      </Tooltip>

      <ShowInAppClient>
        <Tooltip
          title={t('documents.row-actions.delete', { ns: 'components' })}
          placement="top"
        >
          <span>
            <IconButton
              icon="Trashcan"
              variant="icon"
              size="lg"
              color="main"
              onClick={onDeleteClick}
              disabled={isDeleteDisabled}
              data-testid={`documents-row-delete-${document.id}`}
            />
          </span>
        </Tooltip>
      </ShowInAppClient>
    </div>
  );
};
